import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { observer } from 'mobx-react-lite'

import { fetchPendingAssignments } from 'api/dashboardAssignments'

import { fetchPendingDocuments } from 'api/dashboardDocuments'

import { fetchPendingRecommendations } from 'api/dashboardRecommendations'

import {
	fetchPendingFeedbackReplies,
	fetchPendingReviewEssays,
	markFeedbackVersionSeen,
} from 'api/essays'

import DetailsModalAssignment from 'pages/Classroom/components/DetailsModalAssignment/DetailsModalAssignment'
import { ensureApplicationsLoaded } from 'pages/ApplicationsPage/model/ensureApplicationsLoaded'
import { ensureStudentsLoaded } from 'pages/Students/model/ensureStudentsLoaded'
import { studentsStore } from 'pages/Students/model/studentsStore'
import { ensureStudentProfileBuilderLoaded } from 'shared/stores/ensureStudentProfileBuilderLoaded'

import { useTasksPolling } from 'shared/hooks/useTasksPolling'

import { prioritySummaryStore } from 'shared/stores/prioritySummaryStore'

import { priorityTabsStore } from 'shared/stores/priorityTabsStore'

import { syncEssayVersionResolved } from 'shared/utils/counselorSeenSync'

import AssignmentFilters from './components/AssignmentFilters/AssignmentFilters'

import AssignmentTable from './components/AssignmentTable/AssignmentTable'

import DocumentCard from './components/DocumentCard/DocumentCard'

import EssayCard from './components/EssayCard/EssayCard'

import EssayReviewDrawer from './components/EssayReviewDrawer/EssayReviewDrawer'

import FeedbackCard from './components/FeedbackCard/FeedbackCard'

import FeedbackReviewDrawer from './components/FeedbackReviewDrawer/FeedbackReviewDrawer'

import PriorityFilesDrawer from './components/PriorityFilesDrawer/PriorityFilesDrawer'

import PriorityListDrawer from './components/PriorityListDrawer/PriorityListDrawer'

import RecommendationCard from './components/RecommendationCard/RecommendationCard'

import LorReviewDrawer from './components/LorReviewDrawer/LorReviewDrawer'

import GenerateLorModal from 'widgets/GenerateLorModal/GenerateLorModal'

import PdfPreviewPortal from 'pages/ApplicationDetailsPage/components/Checklist/components/PdfPreviewPortal/PdfPreviewPortal'

import { downloadStudentLorFile } from 'api/lorRequests'

import { syncStudentRecommendationsSeen } from 'shared/utils/counselorSeenSync'

import {
	aggregateAssignmentTasks,
	filterAssignmentsLocal,
	gradeAssignmentCounts,
	sortAssignmentsLocal,
} from './utils/assignmentAggregation'

import { getCollectionDeadlineTone } from './utils/priorityCardUi'

import s from './styles/TodaysPrioritySection.module.scss'

import { ReactComponent as EssayIcon } from 'assets/icons/dock.svg'

import { ReactComponent as DocumentsIcon } from 'assets/icons/upload-cloud.svg'

import { ReactComponent as RecommendIcon } from 'assets/icons/dock_pen.svg'

import { ReactComponent as AssignmentIcon } from 'assets/icons/assignment.svg'

const PREVIEW_LIMIT = 6

const RECOMMENDATIONS_PREVIEW_LIMIT = 9

const getEssayVersionRefId = item =>
	String(item?.id || item?.versionId || '').trim()

const getFeedbackVersionRefId = item =>
	String(item?.id || item?.versionId || '').trim()

const tabs = [
	{
		id: 'essays',

		title: 'Essays Waiting Review',

		countLabel: 'to review',

		Icon: EssayIcon,

		tone: 'purple',

		enabled: true,
	},

	{
		id: 'feedback',

		title: 'Feedback Replies',

		countLabel: 'unread',

		Icon: EssayIcon,

		tone: 'success',

		enabled: true,
	},

	{
		id: 'documents',

		title: 'Documents Uploaded',

		countLabel: 'new',

		Icon: DocumentsIcon,

		tone: 'success',

		enabled: true,
	},

	{
		id: 'recommendations',

		title: 'Recommendations',

		countLabel: 'new',

		Icon: RecommendIcon,

		tone: 'purple',

		enabled: true,
	},

	{
		id: 'assignments',

		title: 'Assignment Updates',

		countLabel: 'new',

		Icon: AssignmentIcon,

		tone: 'purple',

		enabled: true,
	},
]

const panelCopy = {
	essays: {
		title: 'Essays Waiting Review',

		loading: 'Loading essays...',

		empty: 'No essays waiting for review.',
	},

	feedback: {
		title: 'Feedback Replies',

		loading: 'Loading feedback...',

		empty: 'No unread feedback from students.',
	},

	documents: {
		title: 'Documents Uploaded',

		loading: 'Loading documents...',

		empty: 'No new documents from students.',
	},

	recommendations: {
		title: 'Recommendations',

		loading: 'Loading recommendations...',

		empty: 'No new LoR activity from students.',

		error: 'Recommendations are available, but the list could not be loaded.',
	},

	assignments: {
		title: 'Assignment Updates',

		loading: 'Loading assignments...',

		empty: 'No assignment updates waiting for review.',
	},
}

const TodaysPrioritySection = ({
	onPendingTotalChange,

	onFeedbackTotalChange,

	forcedTab = null,

	sectionRef = null,
}) => {
	const [activeTab, setActiveTab] = useState('essays')

	const activeTabRef = useRef(activeTab)

	const tabRequestIdsRef = useRef({})

	const emptyRetryCountsRef = useRef({})

	const [essayAllItems, setEssayAllItems] = useState([])

	const [essayListOpen, setEssayListOpen] = useState(false)

	const [essayDetailItems, setEssayDetailItems] = useState([])

	const [essayDetailIndex, setEssayDetailIndex] = useState(null)

	const [essaySeenVersionIds, setEssaySeenVersionIds] = useState(
		() => new Set(),
	)

	const [feedbackAllItems, setFeedbackAllItems] = useState([])

	const [feedbackListOpen, setFeedbackListOpen] = useState(false)

	const [feedbackDetailItems, setFeedbackDetailItems] = useState([])

	const [feedbackDetailIndex, setFeedbackDetailIndex] = useState(null)

	const [feedbackSeenVersionIds, setFeedbackSeenVersionIds] = useState(
		() => new Set(),
	)

	const [documentAllItems, setDocumentAllItems] = useState([])

	const [documentListOpen, setDocumentListOpen] = useState(false)

	const [documentDetailItems, setDocumentDetailItems] = useState([])

	const [documentDetailIndex, setDocumentDetailIndex] = useState(null)

	const [recommendationAllItems, setRecommendationAllItems] = useState([])

	const [recommendationListOpen, setRecommendationListOpen] = useState(false)

	const [lorReviewItem, setLorReviewItem] = useState(null)

	const [lorPdfPreview, setLorPdfPreview] = useState(null)

	const [lorGenerateItem, setLorGenerateItem] = useState(null)

	const [reopenRecommendationListAfterModal, setReopenRecommendationListAfterModal] =
		useState(false)

	const [assignmentGradeTab, setAssignmentGradeTab] = useState('all')

	const [assignmentFilterIds, setAssignmentFilterIds] = useState(['all'])

	const [assignmentSortKey, setAssignmentSortKey] = useState(null)

	const [assignmentSortDir, setAssignmentSortDir] = useState('asc')

	const [selectedAssignment, setSelectedAssignment] = useState(null)

	const [assignmentDrawerOpen, setAssignmentDrawerOpen] = useState(false)

	const essaysTab = priorityTabsStore.getTab('essays')

	const feedbackTab = priorityTabsStore.getTab('feedback')

	const documentsTab = priorityTabsStore.getTab('documents')

	const recommendationsTab = priorityTabsStore.getTab('recommendations')

	const assignmentsTab = priorityTabsStore.getTab('assignments')

	const essayItems = essaysTab.items

	const essayTotal = essaysTab.total

	const feedbackItems = feedbackTab.items

	const feedbackTotal = feedbackTab.total

	const documentItems = documentsTab.items

	const documentTotal = documentsTab.total

	const recommendationItems = recommendationsTab.items

	const recommendationTotal = recommendationsTab.total

	const assignmentRawTasks = assignmentsTab.items

	const activeTabState = priorityTabsStore.getTab(activeTab)

	const loadTab = useCallback(
		async (tabId) => {
			const requestId = (tabRequestIdsRef.current[tabId] || 0) + 1
			tabRequestIdsRef.current[tabId] = requestId
			priorityTabsStore.beginLoad(tabId)

			try {
				let items = []

				let total = 0

				switch (tabId) {
					case 'essays': {
						const res = await fetchPendingReviewEssays({
							limit: PREVIEW_LIMIT,
						})

						items = Array.isArray(res?.data?.items) ? res.data.items : []

						total = Number(res?.data?.total) || items.length

						onPendingTotalChange?.(total)

						break
					}

					case 'feedback': {
						const res = await fetchPendingFeedbackReplies({
							limit: PREVIEW_LIMIT,
						})

						items = Array.isArray(res?.data?.items) ? res.data.items : []

						total = Number(res?.data?.total) || items.length

						break
					}

					case 'documents': {
						const res = await fetchPendingDocuments({ limit: PREVIEW_LIMIT })

						items = Array.isArray(res?.data?.items) ? res.data.items : []

						total = Number(res?.data?.total) || items.length

						break
					}

					case 'recommendations': {
						const res = await fetchPendingRecommendations({
							limit: RECOMMENDATIONS_PREVIEW_LIMIT,
						})

						items = Array.isArray(res?.data?.items) ? res.data.items : []

						total = Number(res?.data?.total) || items.length

						break
					}

					case 'assignments': {
						const res = await fetchPendingAssignments()

						items = Array.isArray(res?.data?.tasks) ? res.data.tasks : []

						total = items.length

						break
					}

					default:
						break
				}

				if (tabRequestIdsRef.current[tabId] !== requestId) return

				const summaryTotal =
					Number(prioritySummaryStore.counts[tabId]) || 0
				const hasNoLocalSuppression =
					(priorityTabsStore.suppressedIds?.[tabId]?.size || 0) === 0
				const shouldRetryUnexpectedEmpty =
					(tabId === 'feedback' ||
						tabId === 'documents' ||
						tabId === 'recommendations' ||
						tabId === 'essays') &&
					items.length === 0 &&
					summaryTotal > 0 &&
					hasNoLocalSuppression

				if (shouldRetryUnexpectedEmpty) {
					const retryCount = emptyRetryCountsRef.current[tabId] || 0
					if (retryCount < 2) {
						emptyRetryCountsRef.current[tabId] = retryCount + 1
						window.setTimeout(() => {
							if (tabRequestIdsRef.current[tabId] === requestId) {
								loadTab(tabId)
							}
						}, 750)
						return
					}

					priorityTabsStore.setError(tabId)
					return
				}

				emptyRetryCountsRef.current[tabId] = 0
				priorityTabsStore.setItems(tabId, items, total)

				if (tabId === 'feedback') {
					onFeedbackTotalChange?.(
						priorityTabsStore.getTab('feedback').total,
					)
				}
			} catch (e) {
				if (tabRequestIdsRef.current[tabId] !== requestId) return
				console.error(e)

				priorityTabsStore.setError(tabId)

				if (!priorityTabsStore.getTab(tabId).fetchedOnce) {
					if (tabId === 'essays') onPendingTotalChange?.(0)

					if (tabId === 'feedback') onFeedbackTotalChange?.(0)
				}
			}
		},

		[onPendingTotalChange, onFeedbackTotalChange],
	)

	useEffect(() => {
		activeTabRef.current = activeTab
	}, [activeTab])

	useEffect(() => {
		loadTab(activeTab)
	}, [activeTab, loadTab])

	useEffect(() => {
		ensureStudentsLoaded().catch(error => {
			console.error(error)
		})
		ensureApplicationsLoaded().catch(error => {
			console.error('Failed to load applications:', error)
		})
		ensureStudentProfileBuilderLoaded().catch(error => {
			console.error('Failed to load profile builder data:', error)
		})
	}, [])

	const refreshActiveRef = useRef(null)

	useEffect(() => {
		refreshActiveRef.current = () => loadTab(activeTabRef.current)
	}, [loadTab])

	useTasksPolling({
		onUpdate: async () => {
			await refreshActiveRef.current?.()

			return priorityTabsStore.getPollingSnapshot(activeTabRef.current)
		},

		enabled: true,

		initialInterval: 30000,

		maxInterval: 300000,

		backoffMultiplier: 1.5,
	})

	const handleSelectTab = useCallback((tabId) => {
		setActiveTab(tabId)
	}, [])

	useEffect(() => {
		if (!forcedTab) return

		handleSelectTab(forcedTab)
	}, [forcedTab, handleSelectTab])

	const tabCounts = {
		essays: prioritySummaryStore.fetchedOnce
			? Number(prioritySummaryStore.counts.essays) || 0
			: essayTotal,

		feedback: prioritySummaryStore.fetchedOnce
			? Number(prioritySummaryStore.counts.feedback) || 0
			: feedbackTotal,

		documents: prioritySummaryStore.fetchedOnce
			? Number(prioritySummaryStore.counts.documents) || 0
			: documentTotal,

		assignments: Number(prioritySummaryStore.counts.assignments) || 0,

		recommendations: prioritySummaryStore.fetchedOnce
			? Number(prioritySummaryStore.counts.recommendations) || 0
			: recommendationTotal,
	}

	const tabUrgencyTone = useMemo(
		() => ({
			essays: getCollectionDeadlineTone(essayItems),

			feedback: getCollectionDeadlineTone(feedbackItems),

			documents: getCollectionDeadlineTone(documentItems),

			assignments: 'purple',

			recommendations: getCollectionDeadlineTone(recommendationItems),
		}),

		[essayItems, feedbackItems, documentItems, recommendationItems],
	)

	const studentGradeById = new Map()
	;(studentsStore.students || []).forEach(student => {
		const id = String(student?.id ?? '').trim()
		if (id) studentGradeById.set(id, student?.grade ?? null)
	})

	const assignmentItems = aggregateAssignmentTasks(
		assignmentRawTasks,
		studentGradeById,
	)

	const assignmentGradeCounts = useMemo(
		() => gradeAssignmentCounts(assignmentItems),

		[assignmentItems],
	)

	const filteredAssignmentItems = useMemo(() => {
		const filtered = filterAssignmentsLocal(assignmentItems, {
			gradeTab: assignmentGradeTab,

			activeFilterIds: assignmentFilterIds,
		})

		return sortAssignmentsLocal(filtered, assignmentSortKey, assignmentSortDir)
	}, [
		assignmentItems,

		assignmentGradeTab,

		assignmentFilterIds,

		assignmentSortKey,

		assignmentSortDir,
	])

	const activePanel = panelCopy[activeTab] || panelCopy.essays

	const isEssaysTab = activeTab === 'essays'

	const isFeedbackTab = activeTab === 'feedback'

	const isDocumentsTab = activeTab === 'documents'

	const isRecommendationsTab = activeTab === 'recommendations'

	const isAssignmentsTab = activeTab === 'assignments'

	const activeTone =
		tabs.find((tab) => tab.id === activeTab)?.tone === 'success'
			? 'success'
			: 'purple'

	const previewItems = isEssaysTab
		? essayItems
		: isFeedbackTab
			? feedbackItems
			: isDocumentsTab
				? documentItems
				: isRecommendationsTab
					? recommendationItems
					: isAssignmentsTab
						? filteredAssignmentItems
						: []

	const previewTotal = isEssaysTab
		? essayTotal
		: isFeedbackTab
			? feedbackTotal
			: isDocumentsTab
				? documentTotal
				: isRecommendationsTab
					? recommendationTotal
					: isAssignmentsTab
						? assignmentItems.length
						: 0

	const showLoadingText =
		activeTabState.loadStatus === 'loading' && activeTabState.items.length === 0

	const showEmpty =
		activeTabState.fetchedOnce &&
		activeTabState.loadStatus === 'success' &&
		(isAssignmentsTab
			? assignmentItems.length === 0
			: activeTabState.items.length === 0)

	const showLoadError = activeTabState.loadStatus === 'error'

	const showRefreshing = activeTabState.loadStatus === 'refreshing'

	const showGrid = !isAssignmentsTab && previewItems.length > 0

	const markEssaySeenLocal = useCallback(versionId => {
		const id = String(versionId || '').trim()
		if (!id) return
		setEssaySeenVersionIds(prev => {
			if (prev.has(id)) return prev
			const next = new Set(prev)
			next.add(id)
			return next
		})
	}, [])

	const withEssaySeenState = useCallback(
		item => {
			if (!item) return item
			const refId = getEssayVersionRefId(item)
			if (!item.isNew || !refId || !essaySeenVersionIds.has(refId)) return item
			return { ...item, isNew: false }
		},
		[essaySeenVersionIds],
	)

	const resolveEssayPending = useCallback(
		(refId, essayId) => {
			const id = String(refId || '').trim()
			if (!id) return
			syncEssayVersionResolved({ versionId: id, essayId })
			onPendingTotalChange?.(
				Math.max(0, Number(prioritySummaryStore.counts.essays) || 0),
			)
			setEssayDetailItems(prev =>
				prev.filter(it => getEssayVersionRefId(it) !== id),
			)
			setEssayAllItems(prev =>
				prev.filter(it => getEssayVersionRefId(it) !== id),
			)
		},
		[onPendingTotalChange],
	)

	const markFeedbackSeenLocal = useCallback(item => {
		const id = getFeedbackVersionRefId(item)
		if (!id) return
		setFeedbackSeenVersionIds(prev => {
			if (prev.has(id)) return prev
			const next = new Set(prev)
			next.add(id)
			return next
		})
		const anchorAt =
			item?.lastMessageAt ||
			item?.previewMessage?.createdAt ||
			undefined
		markFeedbackVersionSeen(id, { anchorAt }).catch(() => {})
	}, [])

	const withFeedbackSeenState = useCallback(
		item => {
			if (!item) return item
			const refId = getFeedbackVersionRefId(item)
			if (!refId || !feedbackSeenVersionIds.has(refId)) {
				return item
			}
			return { ...item, _localSeen: true, isNew: false }
		},
		[feedbackSeenVersionIds],
	)

	const resolveFeedbackPending = useCallback(
		refId => {
			const id = String(refId || '').trim()
			if (!id) return
			onFeedbackTotalChange?.(
				Math.max(0, Number(prioritySummaryStore.counts.feedback) || 0),
			)
			setFeedbackDetailItems(prev =>
				prev.filter(it => getFeedbackVersionRefId(it) !== id),
			)
			setFeedbackAllItems(prev =>
				prev.filter(it => getFeedbackVersionRefId(it) !== id),
			)
		},
		[onFeedbackTotalChange],
	)

	const openEssayAll = async () => {
		try {
			const res = await fetchPendingReviewEssays()

			const nextItems = Array.isArray(res?.data?.items) ? res.data.items : []

			setEssayAllItems(nextItems)

			setEssayListOpen(true)
		} catch (e) {
			console.error(e)

			setEssayAllItems([])

			setEssayListOpen(true)
		}
	}

	const openFeedbackAll = async () => {
		try {
			const res = await fetchPendingFeedbackReplies()

			const nextItems = Array.isArray(res?.data?.items) ? res.data.items : []

			setFeedbackAllItems(nextItems)

			setFeedbackListOpen(true)
		} catch (e) {
			console.error(e)

			setFeedbackAllItems([])

			setFeedbackListOpen(true)
		}
	}

	const openEssayDetail = (sourceItems, idx) => {
		const item = sourceItems[idx]
		const refId = getEssayVersionRefId(item)
		if (refId) markEssaySeenLocal(refId)
		setEssayDetailItems(sourceItems)
		setEssayDetailIndex(idx)
	}

	const handleEssayNavigate = useCallback(
		nextIndex => {
			const target = essayDetailItems[nextIndex]
			const refId = getEssayVersionRefId(target)
			if (refId) markEssaySeenLocal(refId)
			setEssayDetailIndex(nextIndex)
		},
		[essayDetailItems, markEssaySeenLocal],
	)

	const openFeedbackDetail = (sourceItems, idx) => {
		const item = sourceItems[idx]
		if (item) markFeedbackSeenLocal(item)
		setFeedbackDetailItems(sourceItems)
		setFeedbackDetailIndex(idx)
	}

	const handleFeedbackNavigate = useCallback(
		nextIndex => {
			const target = feedbackDetailItems[nextIndex]
			if (target) markFeedbackSeenLocal(target)
			setFeedbackDetailIndex(nextIndex)
		},
		[feedbackDetailItems, markFeedbackSeenLocal],
	)

	const handleEssayListReview = (idx) => {
		setEssayListOpen(false)

		openEssayDetail(essayAllItems, idx)
	}

	const handleFeedbackListOpen = (idx) => {
		setFeedbackListOpen(false)

		openFeedbackDetail(feedbackAllItems, idx)
	}

	const handleCloseEssayDetail = () => {
		setEssayDetailIndex(null)

		loadTab('essays')
	}

	const handleCloseFeedbackDetail = () => {
		setFeedbackDetailIndex(null)

		loadTab('feedback')
	}

	const openDocumentsAll = async () => {
		try {
			const res = await fetchPendingDocuments()

			const nextItems = Array.isArray(res?.data?.items) ? res.data.items : []

			setDocumentAllItems(nextItems)

			setDocumentListOpen(true)
		} catch (e) {
			console.error(e)

			setDocumentAllItems([])

			setDocumentListOpen(true)
		}
	}

	const openRecommendationsAll = useCallback(async () => {
		try {
			const res = await fetchPendingRecommendations()

			const nextItems = Array.isArray(res?.data?.items) ? res.data.items : []

			setRecommendationAllItems(nextItems)

			setRecommendationListOpen(true)
		} catch (e) {
			console.error(e)

			setRecommendationAllItems([])

			setRecommendationListOpen(true)
		}
	}, [])

	const markRecommendationSeen = useCallback(item => {
		if (!item?.studentId) return
		syncStudentRecommendationsSeen(item.studentId, {
			anchorAt: item.lastUpdatedAt || undefined,
			applicationTargetId: item.applicationTargetId,
			cardId: item.id,
			status: item.status,
		})
	}, [])

	const closeLorPdfPreview = useCallback(() => {
		setLorPdfPreview(prev => {
			if (prev?.url && String(prev.url).startsWith('blob:')) {
				URL.revokeObjectURL(prev.url)
			}
			return null
		})
		loadTab('recommendations')
		if (reopenRecommendationListAfterModal) {
			setReopenRecommendationListAfterModal(false)
			void openRecommendationsAll()
		}
	}, [reopenRecommendationListAfterModal, loadTab, openRecommendationsAll])

	const closeLorGenerate = useCallback(() => {
		setLorGenerateItem(null)
		loadTab('recommendations')
		if (reopenRecommendationListAfterModal) {
			setReopenRecommendationListAfterModal(false)
			void openRecommendationsAll()
		}
	}, [reopenRecommendationListAfterModal, loadTab, openRecommendationsAll])

	const openLorPdf = useCallback(
		async (item, file) => {
			if (!item?.studentId || !file?.id) return
			const fromList = recommendationListOpen
			if (fromList) {
				setRecommendationListOpen(false)
				setReopenRecommendationListAfterModal(true)
			}
			setLorReviewItem(null)
			markRecommendationSeen(item)
			try {
				const blob = await downloadStudentLorFile(item.studentId, file.id)
				const url = URL.createObjectURL(blob)
				setLorPdfPreview({
					url,
					title: file.title || 'LoR',
				})
			} catch (e) {
				console.error(e)
				if (fromList) {
					setReopenRecommendationListAfterModal(false)
					setRecommendationListOpen(true)
				}
			}
		},
		[recommendationListOpen, markRecommendationSeen],
	)

	const handleRecommendationView = useCallback(
		item => {
			const files = Array.isArray(item?.uploadedFiles) ? item.uploadedFiles : []
			if (files.length > 1) {
				const fromList = recommendationListOpen
				if (fromList) {
					setRecommendationListOpen(false)
					setReopenRecommendationListAfterModal(true)
				}
				setLorReviewItem(item)
				return
			}
			if (files[0]) {
				openLorPdf(item, files[0])
			}
		},
		[openLorPdf, recommendationListOpen],
	)

	const handleRecommendationCreate = useCallback(
		item => {
			const fromList = recommendationListOpen
			if (fromList) {
				setRecommendationListOpen(false)
				setReopenRecommendationListAfterModal(true)
			}
			setLorReviewItem(null)
			markRecommendationSeen(item)
			setLorGenerateItem(item)
		},
		[recommendationListOpen, markRecommendationSeen],
	)

	const handleRecommendationReview = useCallback(
		item => {
			const fromList = recommendationListOpen
			if (fromList) {
				setRecommendationListOpen(false)
				setReopenRecommendationListAfterModal(true)
			}
			setLorReviewItem(item)
		},
		[recommendationListOpen],
	)

	const handleCloseLorReview = useCallback(() => {
		setLorReviewItem(null)
		if (reopenRecommendationListAfterModal) {
			setReopenRecommendationListAfterModal(false)
			void openRecommendationsAll()
		}
	}, [reopenRecommendationListAfterModal, openRecommendationsAll])

	const openDocumentDetail = (sourceItems, idx) => {
		setDocumentDetailItems(sourceItems)
		setDocumentDetailIndex(idx)
	}

	const handleDocumentListOpen = (idx) => {
		setDocumentListOpen(false)

		openDocumentDetail(documentAllItems, idx)
	}

	const handleCloseDocumentDetail = () => {
		setDocumentDetailIndex(null)

		loadTab('documents')
	}

	const previewDocsCount = useMemo(
		() =>
			documentItems.reduce(
				(acc, it) =>
					acc + (Array.isArray(it?.newDocuments) ? it.newDocuments.length : 0),

				0,
			),

		[documentItems],
	)

	const previewRecsCount = useMemo(
		() => recommendationItems.length,

		[recommendationItems],
	)

	const panelMetric = isFeedbackTab
		? `${feedbackTotal}`
		: isDocumentsTab
			? `${previewDocsCount}`
			: isRecommendationsTab
				? `${previewRecsCount}`
				: isAssignmentsTab
					? `${tabCounts.assignments}`
					: `${essayTotal}`

	const handleViewAll = isEssaysTab
		? openEssayAll
		: isFeedbackTab
			? openFeedbackAll
			: isDocumentsTab
				? openDocumentsAll
				: isRecommendationsTab
					? openRecommendationsAll
					: null

	const handleAssignmentSortChange = useCallback(({ opt }) => {
		if (!opt?.key || !opt?.dir) return

		setAssignmentSortKey(opt.key)

		setAssignmentSortDir(opt.dir)
	}, [])

	const openAssignmentDetail = useCallback(
		(assignment) => {
			setSelectedAssignment(assignment)

			setAssignmentDrawerOpen(true)

			ensureStudentsLoaded().catch(error => {
				console.error(error)
			})
			ensureApplicationsLoaded().catch(error => {
				console.error('Failed to load applications:', error)
			})
			ensureStudentProfileBuilderLoaded().catch(error => {
				console.error('Failed to load profile builder data:', error)
			})

		},
		[],
	)

	return (
		<section className={s.wrap} ref={sectionRef}>
			<h2 className={s.heading}>Today's Priority</h2>

			<div className={s.surface}>
				<div className={s.tabs}>
					{tabs.map((tab) => {
						const isActive = tab.id === activeTab

						const Icon = tab.Icon

						const count = tabCounts[tab.id] ?? 0

						const urgencyClass =
							tabUrgencyTone[tab.id] === 'urgent'
								? s.toneUrgent
								: tabUrgencyTone[tab.id] === 'success'
									? s.toneSuccess
									: count > 0
										? s.tonePurple
										: ''

						return (
							<button
								key={tab.id}
								type="button"
								className={[
									s.tabCard,

									urgencyClass,

									isActive ? s.tabCardActive : '',
								]

									.filter(Boolean)

									.join(' ')}
								disabled={!tab.enabled}
								onClick={() => tab.enabled && handleSelectTab(tab.id)}
							>
								<span className={s.tabHead}>
									<span className={s.tabIcon}>{Icon ? <Icon /> : null}</span>

									<span className={s.tabTitle}>{tab.title}</span>
								</span>

								<span className={s.tabCount}>
									<span className={s.tabCountValue}>{count}</span>

									<span className={s.tabCountLabel}>
										{tab.countLabel || 'items'}
									</span>
								</span>
							</button>
						)
					})}
				</div>

				<div
					className={[
						s.panel,

						activeTone === 'success' ? s.panelSuccess : s.panelPurple,
					].join(' ')}
				>
					<div className={s.panel__head}>
						<h3 className={s.panel__title}>{activePanel.title}</h3>

						<div className={s.panel__actions}>
							{showRefreshing ? (
								<span
									className={s.panel__refreshing}
									title="Refreshing"
									aria-hidden
								/>
							) : null}

							<span className={s.panel__metric}>{panelMetric}</span>

							{previewTotal > previewItems.length && handleViewAll ? (
								<button
									type="button"
									className={s.viewAll}
									onClick={handleViewAll}
								>
									View all
								</button>
							) : null}
						</div>
					</div>

					{showLoadingText ? (
						<p className={s.emptyText}>{activePanel.loading}</p>
					) : null}

					{showEmpty ? (
						<p className={s.emptyText}>{activePanel.empty}</p>
					) : null}

					{showLoadError ? (
						<p className={s.emptyText}>
							{activePanel.error || 'Could not load this list.'}{' '}
							<button type="button" className={s.retryButton} onClick={() => loadTab(activeTab)}>
								Retry
							</button>
						</p>
					) : null}

					{isAssignmentsTab ? (
						<div className={s.assignmentsPanel}>
							<AssignmentFilters
								gradeTab={assignmentGradeTab}
								onGradeTabChange={setAssignmentGradeTab}
								gradeCounts={assignmentGradeCounts}
								activeFilterIds={assignmentFilterIds}
								onActiveFilterChange={setAssignmentFilterIds}
							/>

							<AssignmentTable
								rows={filteredAssignmentItems}
								sortKey={assignmentSortKey}
								sortDir={assignmentSortDir}
								onSortChange={handleAssignmentSortChange}
								onRowClick={openAssignmentDetail}
							/>
						</div>
					) : showGrid ? (
						<div className={s.grid}>
							{isEssaysTab
								? essayItems.map((item, idx) => (
										<EssayCard
											key={item.id || item.versionId}
											item={withEssaySeenState(item)}
											sourceTab="essays"
											onReview={() => openEssayDetail(essayItems, idx)}
										/>
									))
								: isFeedbackTab
									? feedbackItems.map((item, idx) => (
											<FeedbackCard
												key={item.id || item.versionId}
												item={withFeedbackSeenState(item)}
												sourceTab="feedback"
												onOpen={() => openFeedbackDetail(feedbackItems, idx)}
											/>
										))
									: isDocumentsTab
										? documentItems.map((item, idx) => (
												<DocumentCard
													key={item.id || item.studentId}
													item={item}
													sourceTab="documents"
													onOpen={() => openDocumentDetail(documentItems, idx)}
												/>
											))
										: recommendationItems.map(item => (
												<RecommendationCard
													key={item.id || `${item.studentId}:${item.applicationTargetId}`}
													item={item}
													sourceTab="recommendations"
													onView={handleRecommendationView}
													onCreate={handleRecommendationCreate}
													onReview={handleRecommendationReview}
												/>
											))}
						</div>
					) : null}
				</div>
			</div>

			{essayListOpen ? (
				<PriorityListDrawer
					title="Essays Waiting Review"
					subtitle={`${essayAllItems.length} essays pending`}
					items={essayAllItems}
					onClose={() => setEssayListOpen(false)}
					renderItem={(item, idx) => (
						<EssayCard
							item={withEssaySeenState(item)}
							sourceTab="essays"
							onReview={() => handleEssayListReview(idx)}
						/>
					)}
				/>
			) : null}

			{essayDetailIndex != null ? (
				<EssayReviewDrawer
					items={essayDetailItems.map(withEssaySeenState)}
					index={essayDetailIndex}
					onClose={handleCloseEssayDetail}
					onNavigate={handleEssayNavigate}
					onChanged={() => loadTab('essays')}
					onResolvePending={() => {
						const current = essayDetailItems[essayDetailIndex]
						resolveEssayPending(
							getEssayVersionRefId(current),
							current?.essayId,
						)
					}}
				/>
			) : null}

			{feedbackListOpen ? (
				<PriorityListDrawer
					title="Feedback Replies"
					subtitle={`${feedbackAllItems.length} threads with student replies`}
					items={feedbackAllItems}
					onClose={() => setFeedbackListOpen(false)}
					renderItem={(item, idx) => (
						<FeedbackCard
							item={withFeedbackSeenState(item)}
							sourceTab="feedback"
							onOpen={() => handleFeedbackListOpen(idx)}
						/>
					)}
				/>
			) : null}

			{feedbackDetailIndex != null ? (
				<FeedbackReviewDrawer
					items={feedbackDetailItems.map(withFeedbackSeenState)}
					index={feedbackDetailIndex}
					onClose={handleCloseFeedbackDetail}
					onNavigate={handleFeedbackNavigate}
					onChanged={() => loadTab('feedback')}
					onResolvePending={() => {
						const current = feedbackDetailItems[feedbackDetailIndex]
						resolveFeedbackPending(getFeedbackVersionRefId(current))
					}}
				/>
			) : null}

			{documentListOpen ? (
				<PriorityListDrawer
					title="Documents Uploaded"
					subtitle={`${documentAllItems.length} students with new documents`}
					items={documentAllItems}
					onClose={() => setDocumentListOpen(false)}
					renderItem={(item, idx) => (
						<DocumentCard
							item={item}
							sourceTab="documents"
							onOpen={() => handleDocumentListOpen(idx)}
						/>
					)}
				/>
			) : null}

			{documentDetailIndex != null ? (
				<PriorityFilesDrawer
					variant="documents"
					items={documentDetailItems}
					index={documentDetailIndex}
					onClose={handleCloseDocumentDetail}
					onNavigate={setDocumentDetailIndex}
					onChanged={() => loadTab('documents')}
				/>
			) : null}

			{recommendationListOpen ? (
				<PriorityListDrawer
					title="Recommendations"
					subtitle={`${recommendationAllItems.length} new LoR cards`}
					items={recommendationAllItems}
					onClose={() => setRecommendationListOpen(false)}
					renderItem={item => (
						<RecommendationCard
							item={item}
							sourceTab="recommendations"
							onView={handleRecommendationView}
							onCreate={handleRecommendationCreate}
							onReview={handleRecommendationReview}
						/>
					)}
				/>
			) : null}

			{lorReviewItem ? (
				<LorReviewDrawer
					item={lorReviewItem}
					onClose={handleCloseLorReview}
					onViewFile={openLorPdf}
					onCreate={handleRecommendationCreate}
				/>
			) : null}

			<GenerateLorModal
				isOpen={Boolean(lorGenerateItem)}
				onClose={closeLorGenerate}
				application={
					lorGenerateItem
						? {
								id: lorGenerateItem.applicationTargetId,
								applicationTargetId: lorGenerateItem.applicationTargetId,
								studentId: lorGenerateItem.studentId,
								studentName: lorGenerateItem.studentName,
								universityName: lorGenerateItem.universityName,
								programName: lorGenerateItem.programName,
						  }
						: null
				}
				onLetterSaved={() => loadTab('recommendations')}
			/>

			<PdfPreviewPortal
				open={Boolean(lorPdfPreview)}
				url={lorPdfPreview?.url}
				title={lorPdfPreview?.title}
				onClose={closeLorPdfPreview}
			/>

			<DetailsModalAssignment
				isOpen={assignmentDrawerOpen}
				onClose={() => {
					setAssignmentDrawerOpen(false)

					setSelectedAssignment(null)

					loadTab('assignments')
				}}
				assignment={selectedAssignment}
				source="dashboard"
				onAssignmentSynced={() => loadTab('assignments')}
			/>
		</section>
	)
}

export default observer(TodaysPrioritySection)
